import * as React from 'react';
import { useIsomorphicLayoutEffect } from '@cian/react-utils';

const CONTENT_END_SELECTOR = '[data-recommendation-content-end]';
const RECOMMENDATIONS_GRID_SELECTOR = '[data-recommendations-grid]';
const TRANSPORT_LINE_CLAMP_PROPERTY = '--transport-line-clamp';

type TClampElements = {
  transport: HTMLElement;
  trapContentEnd: HTMLElement;
};

export function calculateTransportLineClamp(availableHeight: number, lineHeight: number): number {
  if (!Number.isFinite(availableHeight) || !Number.isFinite(lineHeight) || availableHeight <= 0 || lineHeight <= 0) {
    return 1;
  }

  return Math.max(1, Math.floor(availableHeight / lineHeight));
}

function getClampElements(transport: HTMLElement | null, adjacentTrapPosition?: number): TClampElements | undefined {
  if (!transport || adjacentTrapPosition === undefined) {
    return undefined;
  }

  const recommendationsGrid = transport.closest<HTMLElement>(RECOMMENDATIONS_GRID_SELECTOR);
  const trap = recommendationsGrid?.querySelector<HTMLElement>(
    `[data-recommendation-position="${adjacentTrapPosition}"]`,
  );
  const trapContentEnd = trap?.querySelector<HTMLElement>(CONTENT_END_SELECTOR);

  return trapContentEnd ? { transport, trapContentEnd } : undefined;
}

export function useTransportLineClamp(adjacentTrapPosition?: number): React.RefObject<HTMLElement | null> {
  const transportRef = React.useRef<HTMLElement>(null);

  const updateLineClamp = React.useCallback(() => {
    const transport = transportRef.current;
    const clampElements = getClampElements(transport, adjacentTrapPosition);

    if (!clampElements) {
      transport?.style.removeProperty(TRANSPORT_LINE_CLAMP_PROPERTY);

      return;
    }

    const { transport: transportElement, trapContentEnd } = clampElements;
    const lineHeight = Number.parseFloat(window.getComputedStyle(transportElement).lineHeight);
    const availableHeight =
      trapContentEnd.getBoundingClientRect().bottom - transportElement.getBoundingClientRect().top;
    const lineClamp = calculateTransportLineClamp(availableHeight, lineHeight);

    transportElement.style.setProperty(TRANSPORT_LINE_CLAMP_PROPERTY, String(lineClamp));
  }, [adjacentTrapPosition]);

  useIsomorphicLayoutEffect(() => {
    updateLineClamp();
  }, [updateLineClamp]);

  React.useEffect(() => {
    const clampElements = getClampElements(transportRef.current, adjacentTrapPosition);

    if (!clampElements) {
      return undefined;
    }

    let animationFrame: number | undefined;
    let isMounted = true;

    const scheduleUpdate = () => {
      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame);
      }

      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = undefined;
        updateLineClamp();
      });
    };

    const resizeObserver = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(scheduleUpdate);

    if (resizeObserver) {
      resizeObserver.observe(clampElements.trapContentEnd);
      resizeObserver.observe(clampElements.transport);
    } else {
      window.addEventListener('resize', scheduleUpdate);
    }

    if (document.fonts?.status === 'loading') {
      void document.fonts.ready.then(() => {
        if (isMounted) {
          scheduleUpdate();
        }
      });
    }

    return () => {
      isMounted = false;

      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame);
      }

      resizeObserver?.disconnect();

      if (!resizeObserver) {
        window.removeEventListener('resize', scheduleUpdate);
      }
    };
  }, [adjacentTrapPosition, updateLineClamp]);

  return transportRef;
}
